import type { CSSProperties, ReactElement, ReactNode } from "react";

/**
 * The board vocabulary, shared by every screen.
 *
 * One shape - a labelled panel with a header and a body that scrolls only
 * when it must - so that moving between tabs does not mean learning a new
 * layout each time. The home board established it; the rest of the app reads
 * the same way.
 */

export type Tone = "positive" | "warning" | "negative";

export function Tile({
  title,
  note,
  actions,
  span,
  fills = false,
  className,
  children,
}: {
  readonly title: string;
  readonly note?: string | undefined;
  /** Controls belonging to the tile's content but not inside its body. */
  readonly actions?: ReactNode;
  /** Omitted when the tile is stacked inside another slot. */
  readonly span?: number | undefined;
  /** The content sizes itself, so the body must not scroll. */
  readonly fills?: boolean;
  /** Extra class, for a tile a layout needs to reach (the phone queue). */
  readonly className?: string | undefined;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <section
      className={className === undefined ? "tile" : `tile ${className}`}
      style={span === undefined ? undefined : { gridColumn: `span ${span}` }}
    >
      <header className="tile-head">
        <h3>{title}</h3>
        {actions === undefined ? null : actions}
        {note === undefined ? null : <span>{note}</span>}
      </header>
      <div className={fills ? "tile-body fills" : "tile-body"}>{children}</div>
    </section>
  );
}

/**
 * A single reading. Tone is the money language - green for profit, amber for
 * still running, red for gone - and is never applied decoratively, so a
 * coloured figure always means something has happened to the money.
 */
export function Figure({
  label,
  value,
  tone,
  sub,
}: {
  readonly label: string;
  readonly value: string;
  readonly tone?: Tone | undefined;
  readonly sub?: string | undefined;
}): ReactElement {
  return (
    <div className="figure">
      <span className="figure-label">{label}</span>
      <strong className={tone === undefined ? "" : `is-${tone}`}>
        {value}
      </strong>
      {sub === undefined ? null : <small>{sub}</small>}
    </div>
  );
}

/**
 * The readings that frame a screen, divided by hairlines rather than split
 * into separate cards: they are one group, because they describe one thing.
 */
export function KpiStrip({
  children,
  columns,
}: {
  readonly children: ReactNode;
  readonly columns: number;
}): ReactElement {
  return (
    <section
      className="tile kpi-strip"
      style={{ "--kpi-columns": columns } as CSSProperties}
    >
      {children}
    </section>
  );
}

export function money(cents: number): string {
  return `A$${(cents / 100).toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function signedUnits(units: number): string {
  return `${units >= 0 ? "+" : ""}${units.toFixed(2)}u`;
}

/**
 * A win rate with the range it actually supports.
 *
 * Nine bets at 67% and nine hundred at 67% are not the same claim, and a bare
 * percentage hides which one you are looking at. The Wilson interval is used
 * rather than the textbook normal one because it stays sane at the small
 * counts this operation has - a 6-0 rule has a defined range instead of a
 * meaningless 100% +/- 0.
 */
export function winRateReading(wins: number, bets: number): string {
  if (bets === 0) return "No settled bets";
  const p = wins / bets;
  const z = 1.96;
  const denominator = 1 + (z * z) / bets;
  const centre = (p + (z * z) / (2 * bets)) / denominator;
  const spread =
    (z * Math.sqrt((p * (1 - p)) / bets + (z * z) / (4 * bets * bets))) /
    denominator;
  const low = Math.max(0, centre - spread);
  const high = Math.min(1, centre + spread);
  return (
    `${wins}/${bets} = ${(p * 100).toFixed(0)}% ` +
    `(95% range ${(low * 100).toFixed(0)}-${(high * 100).toFixed(0)}%)`
  );
}
